import { ROAD_CLASSES, type RoadClass } from '@hotlist/shared';

/**
 * Map colors.
 *
 * The palette is a night map read off a console, not a daytime map. Roads sit
 * on near black and get lighter with class, so the through routes are legible
 * at county zoom and the residential grid only resolves when you come down to
 * it. That is deliberate: enforcement should be able to see the arteries at a
 * glance and have to work for the rest.
 */
export const BACKGROUND = 0x05070a;

export const ROAD_COLOR: Record<RoadClass, number> = {
  motorway: 0xf2a83c,
  // Ramps read as the road they serve, a shade down. They are their own class
  // because they are slow, not because they should look like something else.
  motorway_link: 0xc98c33,
  trunk: 0xd98e2e,
  trunk_link: 0xb47828,
  primary: 0xb9c4d0,
  primary_link: 0x9aa5b1,
  secondary: 0x8b98a6,
  secondary_link: 0x76818d,
  tertiary: 0x6b7a8a,
  tertiary_link: 0x5c6975,
  unclassified: 0x556170,
  residential: 0x454f5c,
  service: 0x333c47,
  track: 0x2f3740,
};

/** Minimum on-screen stroke in pixels, so a county-wide view still reads. */
export const ROAD_MIN_PIXELS: Record<RoadClass, number> = {
  motorway: 2.2,
  motorway_link: 1.2,
  trunk: 2.0,
  trunk_link: 1.1,
  primary: 1.6,
  primary_link: 1.0,
  secondary: 1.4,
  secondary_link: 0.9,
  tertiary: 1.1,
  tertiary_link: 0.8,
  unclassified: 0.9,
  residential: 0.7,
  service: 0.6,
  track: 0.6,
};

/**
 * Draw order. Minor roads first so a state highway is not cut by every
 * driveway that crosses it.
 */
export const ROAD_DRAW_ORDER: RoadClass[] = [...ROAD_CLASSES].reverse();

export const PLACE_LABEL_COLOR = 0x9fb0c2;

/**
 * Civilian paint. Close together on purpose.
 *
 * §4: a player scanning traffic should not be able to pick anyone out by
 * color, because the only handle this game gives you on a vehicle is its
 * plate. That rule is about the *spread* -- these eight are within a few
 * percent of each other and always will be -- and it was being read as a rule
 * about brightness, which it is not.
 *
 * Lifted off the road, because they were the same pale blue-grey the roads are
 * drawn in and a small car on a road it matches is a car nobody can see. The
 * spread is unchanged: still nobody's car, still nothing to pick out.
 */
export const CIVILIAN_COLORS = [
  0xe8edf2, 0xd4dbe3, 0xc2cbd5, 0xf1f4f7, 0xdde3ea, 0xcbd4de, 0xf6f8fa, 0xd8dfe7,
];

export const SELECTED_COLOR = 0xffb020;
/** The vehicle this client is driving. */
export const SELF_COLOR = 0x4bd6a0;
/** Somebody else's player vehicle, in solo mode where the client knows. */
export const PLAYER_COLOR = 0x4bd6a0;
export const PATROL_COLOR = 0x5aa9ff;

/**
 * What each road class is called, to a person.
 *
 * The class names are OpenStreetMap tags. They are the right thing to key data
 * on and the wrong thing to show anybody: the panel was telling players they
 * were driving on a "primary_link", which is not a phrase any driver has ever
 * used about a road they were on.
 *
 * The words are Wisconsin's, because the county is. A `primary` here is a state
 * highway and a `secondary` is a County Trunk Highway -- the county's own road
 * names say "County Highway CW" -- so those are what they are called. A `_link`
 * is the slip road on or off something, which everybody calls a ramp.
 */
export const ROAD_WORDS: Record<RoadClass, string> = {
  motorway: 'freeway',
  motorway_link: 'freeway ramp',
  trunk: 'highway',
  trunk_link: 'highway ramp',
  primary: 'state highway',
  primary_link: 'ramp',
  secondary: 'county highway',
  secondary_link: 'ramp',
  tertiary: 'local road',
  tertiary_link: 'ramp',
  unclassified: 'rural road',
  residential: 'residential street',
  service: 'service road',
  track: 'track',
};

/** The plain name for a class, falling back to the tag if one is ever added. */
export function roadWords(cls: string): string {
  return ROAD_WORDS[cls as RoadClass] ?? cls.replace(/_/g, ' ');
}
